import type { AnalysisState, Filter, FilterOperator } from '@aibi/contracts';
import { IconButton, Input, Select } from '@aibi/ui';
import { Funnel, X } from 'lucide-react';
import { useState } from 'react';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { DropZone } from '../dnd/DropZone';
import { ExplorerSection } from './ExplorerSection';
import {
  formatFilterValues,
  OPERATOR_LABELS,
  operatorsFor,
  parseFilterValues,
  takesValues,
} from './filter-operators';

/**
 * Filtros de la visualizacion seleccionada. Es zona de destino: soltar un campo crea un
 * filtro que luego se edita aqui. Los cambios se validan en el reducer.
 */
export function FilterList({ card }: { readonly card: AnalysisState | null }) {
  const { audience } = useWorkspace();
  if (audience === 'viewer') return null;
  return (
    <ExplorerSection title="Filtros" count={card?.filters.length ?? 0}>
      {card === null ? (
        <p className="px-2 py-1 text-xs text-muted-foreground">
          Seleccione una visualizacion para ver sus filtros.
        </p>
      ) : (
        <DropZone
          id={`filters:${card.id}`}
          target={{ type: 'filters', cardId: card.id }}
          label="Zona de filtros"
          className="min-h-9 p-0.5"
        >
          {card.filters.length === 0 ? (
            <p className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-2 py-2 text-xs text-muted-foreground">
              <Funnel aria-hidden className="size-3.5" />
              Arrastre un campo aqui para filtrar.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {card.filters.map((filter) => (
                <FilterRow
                  key={`${filter.id}:${filter.operator}:${formatFilterValues(filter.values)}`}
                  cardId={card.id}
                  filter={filter}
                />
              ))}
            </ul>
          )}
        </DropZone>
      )}
    </ExplorerSection>
  );
}

function FilterRow({ cardId, filter }: { readonly cardId: string; readonly filter: Filter }) {
  const { catalog } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const fieldId =
    filter.target.kind === 'dimension' ? filter.target.dimensionId : filter.target.measureId;
  const field = catalog.get(fieldId);
  const [operator, setOperator] = useState<FilterOperator>(filter.operator);
  const [text, setText] = useState(formatFilterValues(filter.values));

  const commit = (nextOperator: FilterOperator, nextText: string) => {
    execute({
      type: 'update-filter',
      cardId,
      filterId: filter.id,
      operator: nextOperator,
      values: takesValues(nextOperator) ? parseFilterValues(nextText, field) : [],
    });
  };

  return (
    <li className="space-y-1 rounded-md border border-border bg-card p-1.5">
      <div className="flex items-center gap-1">
        <span className="flex-1 truncate text-xs font-medium">{field?.name ?? fieldId}</span>
        <IconButton
          label={`Quitar filtro de ${field?.name ?? fieldId}`}
          className="size-6"
          onClick={() => {
            execute({ type: 'remove-filter', cardId, filterId: filter.id });
          }}
        >
          <X />
        </IconButton>
      </div>
      <Select
        aria-label="Operador"
        value={operator}
        className="h-7 text-xs"
        onChange={(event) => {
          const next = event.target.value as FilterOperator;
          setOperator(next);
          if (!takesValues(next) || text.trim() !== '') commit(next, text);
        }}
      >
        {operatorsFor(field).map((candidate) => (
          <option key={candidate} value={candidate}>
            {OPERATOR_LABELS[candidate]}
          </option>
        ))}
      </Select>
      {takesValues(operator) && (
        <Input
          aria-label="Valores del filtro (separados por coma)"
          placeholder={operator === 'between' ? 'minimo, maximo' : 'valor, valor...'}
          value={text}
          className="h-7 text-xs"
          onChange={(event) => {
            setText(event.target.value);
          }}
          onBlur={() => {
            if (text !== formatFilterValues(filter.values)) commit(operator, text);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit(operator, text);
          }}
        />
      )}
    </li>
  );
}
